/* eslint-disable no-bitwise */
/* eslint-disable max-len */
import React from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import { v4 as uuid } from 'uuid';
import { CivilDate } from 'tc39-proposal-temporal';
import Media from 'react-media';
import { Overlay, Glyphicon } from '@bingads-webui-react/primitive';
import { getCivilDateToday, compareCivilDate, getDaysOfARange, RANGES, isTheSameRange, isRangeValid, isRangeDateAvailable } from '@bingads-webui-universal/temporal-utitlities';
import { WithApplyAndCloseButton } from '@bingads-webui-react/hoc-with-apply-close-button';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import {
  QuickDateLeftRightPicker, getXTimesAgoDate,
  getRangeIndexWithDefaultType, getNthPeriodWithRangeType
} from '@bingads-webui-react/quick-date-left-right-picker';
import { ComparedDateRangeCalendar } from './compared-date-range-calendar';
import { defaultSpecificRanges } from './common/default-specific-ranges';
import { getSelectedRange, formatMonthDayYear, getComparedTypeFromRange } from './common/date-util';
import { ComparedOptions } from './common/compared-options';
import { ComparedDateRangePickerPropTypes, ComparedDateRangePickerDefaultProps } from './prop-types';

const i18nMap = {
  apply: _TL_('Apply'),
  close: _TL_('Close'),
  compare: _TL_('Compare to: '),
  defaultAriaLabel: _TL_('Select a date range'),
};
const yesterday = getCivilDateToday().plus({ days: -1 });
const midDash = selectedRange => `${selectedRange && selectedRange.start ? ' - ' : ''}`;
const getDisplaySelectedDate = (i18n, selectedRange, timeZone, dateFormat) =>
  `${formatMonthDayYear(selectedRange.start, i18n, timeZone, dateFormat)}${midDash(selectedRange)}${formatMonthDayYear(selectedRange.end, i18n, timeZone, dateFormat)}`;
const getDisplaySelectedDateWithType = (i18n, selectedType, selectedRange, timeZone, dateFormat) =>
  `${selectedType ? selectedType.text : ''}(${getDisplaySelectedDate(i18n, selectedRange, timeZone, dateFormat)})`;
const getMainDatePickerDateDisplay = (i18n, selectedType, selectedRange, timeZone, dateFormat) =>
  `${selectedType ? selectedType.text : ''}${selectedType ? ':' : ''} ${getDisplaySelectedDate(i18n, selectedRange, timeZone, dateFormat)}`;

const getXTimesAgoMainDatePickerDateDisplay = (i18n, selectedType, selectedRange, rangeIndex, timeZone, dateFormat) =>
  `${selectedType ? getXTimesAgoDate(i18n, selectedType, rangeIndex) : ''}${selectedType ? ':' : ''} ${getDisplaySelectedDate(i18n, selectedRange, timeZone, dateFormat)}`;

let globalEvent = null;

export class ComparedDateRangePicker extends React.PureComponent {
  constructor(props) {
    super(props);

    const todayObj = this.props.i18n.getToday({ timeZone: props.timeZone });
    this.inputRef = React.createRef();
    this.today = new CivilDate(todayObj.year, todayObj.month, todayObj.day);
    this.minDate = this.props.minDate || this.today.plus({ years: -3 });
    this.maxDate = this.props.maxDate || this.today;
    const { specificRanges = [], rangeIds = [] } = props;
    if (specificRanges.length) {
      this.specificRanges = specificRanges;
    } else if (rangeIds && rangeIds.length) {
      this.specificRanges = defaultSpecificRanges(props.i18n, props.timeZone, this.minDate, rangeIds);
    } else {
      this.specificRanges = defaultSpecificRanges(props.i18n, props.timeZone, this.minDate);
    }

    let preSelectedRange;
    let needUpdateInFirstTime = false;
    if (!props.selectedRange || !props.selectedRange.start || !props.selectedRange.end) {
      preSelectedRange = { start: yesterday, end: yesterday };
      needUpdateInFirstTime = true;
    } else {
      preSelectedRange = props.selectedRange;
    }
    let selectedRange;
    const rangeType = _.find(this.specificRanges, range => range.rangeId === props.rangeId);
    const { rangeDays } = props;
    let { rangeIndex = getRangeIndexWithDefaultType(rangeType) } = props;
    if (props.shouldShowQuickLeftRightPicker && props.rangeIndex && props.rangeId !== RANGES.Custom) {
      // Fix the selectedRange & the rangeIndex
      const rangeWithIndex = getNthPeriodWithRangeType({ rangeId: props.rangeId }, rangeIndex, this.minDate, this.maxDate, rangeDays);
      const { rangeIndex: cRangeIndex, range } = rangeWithIndex;
      selectedRange = range;
      rangeIndex = cRangeIndex;
    } else {
      selectedRange = getSelectedRange(preSelectedRange, this.specificRanges, props.rangeId);
    }

    // update the comparedOption according to the comparedTypeKey
    const comparedOption =
      _.find(ComparedOptions, option => option.keyToExport === props.comparedTypeKey);
    let selectedComparedRange = props.comparedTypeKey && props.comparedTypeKey !== 'customPeriod' && comparedOption ?
      comparedOption.get(selectedRange, props.rangeId) : props.selectedComparedRange;
    selectedComparedRange = isRangeDateAvailable(selectedComparedRange, this.minDate, this.maxDate) ? selectedComparedRange : { start: undefined, end: undefined };
    if (needUpdateInFirstTime) {
      props.onPickDateRanges({
        selectedRange,
        selectedComparedRange,
        comparedOption,
        rangeType: _.find(this.specificRanges, range => range.rangeId === RANGES.Yesterday),
      });
    }
    let needUpdateBecauseOfCorrection = false;
    needUpdateBecauseOfCorrection = ((!!props.selectedRange.start ^ !!selectedRange.start) ||
      (!!props.selectedRange.start && !!selectedRange.start && compareCivilDate(props.selectedRange.start, selectedRange.start) !== 0))
      || ((!!props.selectedRange.end ^ !!selectedRange.end) ||
        (!!props.selectedRange.end && !!selectedRange.end && compareCivilDate(props.selectedRange.end, selectedRange.end) !== 0))
      || ((!!props.selectedComparedRange.start ^ !!selectedComparedRange.start) ||
        (!!props.selectedComparedRange.start && !!selectedComparedRange.start && compareCivilDate(props.selectedComparedRange.start, selectedComparedRange.start) !== 0))
      || ((!!props.selectedComparedRange.end ^ !!selectedComparedRange.end) ||
        (!!props.selectedComparedRange.end && !!selectedComparedRange.end && compareCivilDate(props.selectedComparedRange.end, selectedComparedRange.end) !== 0));
    if (!needUpdateInFirstTime && needUpdateBecauseOfCorrection) {
      props.onPickDateRanges({
        selectedRange,
        selectedComparedRange,
        comparedOption,
        rangeType,
        rangeIndex,
        rangeDays,
      });
    }
    if (this.props.dateRangeLogger) {
      this.props.dateRangeLogger({
        method: 'constructor',
        rangeType,
        selectedRangeStart: selectedRange.start.toString(),
        selectedRangeEnd: selectedRange.end.toString(),
        selectedComparedRangeStart: selectedComparedRange.start ?
          selectedComparedRange.start.toString() :
          null,
        selectedComparedRangeEnd: selectedComparedRange.end ?
          selectedComparedRange.end.toString() :
          null,
        rangeIndex,
        rangeDays,
      });
    }
    this.state = {
      dialogVisible: false,
      comparedOn: !!props.comparedTypeKey,
      rangeType,
      selectedRange,
      selectedComparedRange,
      comparedOption,
      rangeIndex,
      rangeDays,
    };
    this.isToggleChecked = !!props.comparedTypeKey;
  }
  onQuickApply = ({
    selectedRange,
    selectedComparedRange,
    rangeId,
    rangeIndex,
  }) => {
    // debounce the global date update
    if (globalEvent) {
      // use the globalEvent for the new date picker DOM
      clearTimeout(globalEvent);
    }

    const ranges = this.specificRanges;
    const rangeType = _.find(ranges, range => range.rangeId === rangeId);
    const {
      comparedOption,
      rangeDays,
    } = this.state;

    if ((!isRangeValid(selectedComparedRange)) && this.state.comparedOn !== false) {
      this.setState(() => ({
        rangeType,
        selectedComparedRange,
        selectedRange,
        rangeIndex,
        comparedOn: false,
      }));
      // for turning off the compared mode, we don't do the debounce
      if (this.props.onPickDateRanges) {
        this.props.onPickDateRanges({
          selectedRange,
          selectedComparedRange,
          comparedOption,
          rangeType,
          rangeIndex,
          rangeDays,
        });
      }
      // switchCompared should be after onPickDateRanges
      this.props.onSwitchCompare({ isChecked: false });
    } else {
      this.setState(() => ({
        rangeType,
        selectedComparedRange,
        selectedRange,
        rangeIndex,
      }));
      globalEvent = setTimeout(() => {
        if (this.props.onPickDateRanges) {
          this.props.onPickDateRanges({
            selectedRange,
            selectedComparedRange,
            comparedOption,
            rangeType,
            rangeIndex,
            rangeDays,
          });
        }
      }, 250);
    }

    this.onDismiss();

    // Should not debounce logging operation
    if (this.props.dateRangeLogger) {
      this.props.dateRangeLogger({
        method: 'onQuickApply',
        rangeType,
        selectedRangeStart: selectedRange.start.toString(),
        selectedRangeEnd: selectedRange.end.toString(),
        selectedComparedRangeStart: selectedComparedRange.start ?
          selectedComparedRange.start.toString() :
          null,
        selectedComparedRangeEnd: selectedComparedRange.end ?
          selectedComparedRange.end.toString() :
          null,
        rangeIndex,
        rangeDays,
      });
    }
  };
  onApply = ({
    selectedRange,
    selectedComparedRange,
    comparedOption,
    rangeId,
  }) => {
    const ranges = this.specificRanges;
    const rangeType = _.find(ranges, range => range.rangeId === rangeId);
    let { rangeIndex } = this.state;
    const rangeDays = getDaysOfARange(selectedRange);
    let newComparedRange = { start: undefined, end: undefined };
    if (isRangeDateAvailable(selectedComparedRange, this.minDate, this.maxDate)) {
      newComparedRange = selectedComparedRange;
      this.isToggleChecked = true;
    } else {
      this.isToggleChecked = false;
    }

    if (this.props.shouldShowQuickLeftRightPicker) {
      // Check if we switch to the new range Type, or reselect the same Type
      const currentRangeId = this.state.rangeType && this.state.rangeType.rangeId;
      if (rangeId !== currentRangeId) {
        rangeIndex = getRangeIndexWithDefaultType(rangeType);
      } else if (rangeId !== RANGES.Custom) {
        const selectedSpecificRange = _.find(this.specificRanges, range => range.rangeId === rangeId);
        if (selectedSpecificRange && isTheSameRange(selectedRange, selectedSpecificRange.range)) {
          rangeIndex = getRangeIndexWithDefaultType(rangeType);
        }
      }
    }

    this.setState({
      rangeType,
      selectedRange,
      selectedComparedRange: newComparedRange,
      comparedOption,
      rangeIndex,
      rangeDays,
    });
    if (this.props.dateRangeLogger) {
      this.props.dateRangeLogger({
        method: 'onApply',
        rangeType,
        selectedRangeStart: selectedRange.start.toString(),
        selectedRangeEnd: selectedRange.end.toString(),
        newComparedRangeStart: newComparedRange.start ?
          newComparedRange.start.toString() :
          null,
        newComparedRangeEnd: newComparedRange.end ?
          newComparedRange.end.toString() :
          null,
        selectedComparedRangeStart: selectedComparedRange.start ?
          selectedComparedRange.start.toString() :
          null,
        selectedComparedRangeEnd: selectedComparedRange.end ?
          selectedComparedRange.end.toString() :
          null,
        rangeIndex,
        rangeDays,
      });
    }
    this.onDismiss();

    if (this.props.onPickDateRanges) {
      this.props.onPickDateRanges({
        selectedRange,
        selectedComparedRange: newComparedRange,
        comparedOption,
        rangeType,
        rangeIndex,
        rangeDays,
      });
    }
    if (this.isToggleChecked !== this.state.comparedOn) {
      this.setState({ comparedOn: this.isToggleChecked });
      this.props.onSwitchCompare({ isChecked: this.isToggleChecked });
    }
  };
  onDismiss = () => {
    this.setState({ dialogVisible: false });
  };
  onInputClick = () => {
    this.setState(preState => ({ dialogVisible: !preState.dialogVisible }));
  };
  onSwitchCompare = ({ isChecked }) => {
    this.isToggleChecked = isChecked;
  };
  render() {
    const {
      disableCompare,
      displayType,
      isMainDatePicker,
      i18n,
      relativeDropdown,
      timeZone,
      dateFormat,
      isMonthRangePicker,
      isAriaLabelTranslated,
      shouldShowQuickLeftRightPicker,
    } = this.props;

    const {
      rangeType,
      selectedComparedRange,
      selectedRange,
      comparedOn,
      dialogVisible,
      rangeIndex,
      rangeDays,
    } = this.state;
    this.isToggleChecked = comparedOn;
    const shouldShowComparedInInput = !disableCompare && comparedOn && selectedComparedRange && selectedComparedRange.start && selectedComparedRange.end;

    this.ComparedDateRangeCalendarWithApplyAndClose = sizeClass =>
      WithApplyAndCloseButton({
        onApply: this.onApply,
        onDismiss: this.onDismiss,
        applyText: this.props.i18n.getString(i18nMap.apply),
        closeText: this.props.i18n.getString(i18nMap.close),
        className: classNames(
          'compared-date-range-picker-dialog', sizeClass,
          shouldShowComparedInInput ? 'double-row' : 'single-row'
        ),
      })(ComparedDateRangeCalendar);

    const ComparedCalendar = (sizeClass) => {
      // eslint-disable-next-line max-len
      const ComparedDateRangeCalendarWithApplyAndClose = this.ComparedDateRangeCalendarWithApplyAndClose(sizeClass);

      return (
        <ComparedDateRangeCalendarWithApplyAndClose
          disableCompare={disableCompare}
          i18n={i18n}
          onPickDateRanges={this.onApply}
          onSwitchCompare={this.onSwitchCompare}
          selectedRange={selectedRange}
          selectedComparedRange={selectedComparedRange}
          selectedRangeId={rangeType && rangeType.rangeId} // propagate the selected rangeId to the calendar
          specificRanges={this.specificRanges}
          timeZone={timeZone}
          dateFormat={dateFormat}
          today={this.today}
          minDate={this.minDate}
          maxDate={this.maxDate}
          isMonthRangePicker={isMonthRangePicker}
          isAriaLabelTranslated={isAriaLabelTranslated}
          dateRangeLogger={this.props.dateRangeLogger}
        />
      );
    };
    const ResponsiveComparedCalendar = (
      <Media query={{ maxHeight: 699 }}>
        {matches => (
          ComparedCalendar(matches ? 'smallCalendar' : 'largeCalendar')
        )}
      </Media>
    );

    let displaySelectedDate = '';
    if (isMainDatePicker) {
      displaySelectedDate = shouldShowQuickLeftRightPicker ? getXTimesAgoMainDatePickerDateDisplay(i18n, rangeType, selectedRange, rangeIndex, timeZone, dateFormat) :
        getMainDatePickerDateDisplay(i18n, rangeType, selectedRange, timeZone, dateFormat);
    } else {
      displaySelectedDate = displayType ?
        getDisplaySelectedDateWithType(i18n, rangeType, selectedRange, timeZone, dateFormat) :
        getDisplaySelectedDate(i18n, selectedRange, timeZone, dateFormat);
    }

    const campaignUICalendar = (
      <Overlay
        className="date-picker-modal"
        show={this.state.dialogVisible}
        onHide={this.onDismiss}
        animation={false}
        rootClose
        placement="bottom"
        container={this}
        target={this}
      >
        <FocusLock
          onExit={this.onDismiss}
          focusFirstItemOnMount
        >
          {ResponsiveComparedCalendar}
        </FocusLock>
      </Overlay>
    );

    const ariaLabelProp = (!this.props.ariaLabelledBy && !this.props.ariaLabel)
      ? { 'aria-label': i18n.getString(i18nMap.defaultAriaLabel) }
      : _.omit({
        'aria-labelledby': this.props.ariaLabelledBy,
        'aria-label': this.props.ariaLabel,
      }, value => !value);

    // Use this unique suffix to avoid duplicate id if add 2 component into one page.
    const uniqueSuffix = `${uuid().substring(0, 8)}`;
    return (
      <div className="compared-date-range-picker">
        <div className="compared-date-range-picker-input">
          <button
            className={classNames(
              'range-display',
              dialogVisible ? 'dialog-visible' : 'dialog-invisible',
              shouldShowComparedInInput ? 'double-row' : 'single-row'
            )}
            onClick={this.onInputClick}
            value={displaySelectedDate}
            readOnly
            ref={this.inputRef}
            {...ariaLabelProp}
            aria-haspopup
            aria-expanded={this.state.dialogVisible}
            aria-describedby={`selected-row-${uniqueSuffix} compared-row-${uniqueSuffix}`}
          >
            <div className="selected-row" id={`selected-row-${uniqueSuffix}`}>
              {displaySelectedDate}
            </div>
            {shouldShowComparedInInput && (
              <div className="compared-row" id={`compared-row-${uniqueSuffix}`}>
                {`${i18n.getString(i18nMap.compare)}${getDisplaySelectedDate(i18n, selectedComparedRange, timeZone, dateFormat)}`}
              </div>
            )}
          </button>
          <Glyphicon
            glyph="ba-Calendar"
            onClick={this.onInputClick}
          />
          {shouldShowQuickLeftRightPicker &&
            (<QuickDateLeftRightPicker
              shouldShowComparedInInput={shouldShowComparedInInput}
              onPickDateRanges={this.onQuickApply}
              rangeType={rangeType}
              selectedRange={selectedRange}
              rangeIndex={rangeIndex}
              minDate={this.minDate}
              maxDate={this.maxDate}
              rangeDays={rangeDays}
              comparedOption={getComparedTypeFromRange({
                range: selectedRange,
                comparedRange: selectedComparedRange,
                selectedRangeId: rangeType && rangeType.rangeId,
              })}
              selectedComparedRange={selectedComparedRange}
              dateRangeLogger={this.props.dateRangeLogger}
            />)}
        </div>
        {relativeDropdown ?
          <Overlay
            show={this.state.dialogVisible}
            onHide={this.onDismiss}
            animation={false}
            rootClose
            placement="right"
            container={this}
          >
            <div id="overlayContainer">
              <div className="date-picker-modal overlay-dialog">
                <div className="compared-date-range-picker-dialog">
                  <FocusLock
                    onExit={this.onDismiss}
                    focusFirstItemOnMount
                  >
                    {ComparedCalendar()}
                  </FocusLock>
                </div>
              </div>
            </div>
          </Overlay>
          :
          campaignUICalendar
        }
      </div>
    );
  }
}

ComparedDateRangePicker.propTypes = ComparedDateRangePickerPropTypes;
ComparedDateRangePicker.defaultProps = ComparedDateRangePickerDefaultProps;
